<template>
  <div class="i-task-box">
    <div class="top">
      <div class="step">
        <div class="check">
          <input
            type="checkbox"
            class="checkbox"
            :checked="isChecked"
            :disabled="props.isChecked !== null"
            @click="handleCheckboxClick"
          />
        </div>
        <span>Step {{ props.index }}</span>
      </div>
      <div class="head">
        <div>{{ props.header }}</div>
        <img
          class="chevron"
          :src="data.isCollapsed ? iconChevronDown : iconChevronUp"
          @click="data.isCollapsed = !data.isCollapsed"
        />
      </div>
    </div>
    <div
      v-show="!data.isCollapsed"
      class="collapsible"
    >
      <div
        v-if="props.subheader"
        class="sub"
        v-html="props.subheader"
      ></div>
      <slot name="collapsible">Loader Subtitle</slot>
      <div
        v-if="props.actionText"
        class="action"
      >
        <iBaseButton
          v-bind="buttonStyle"
          :text="props.actionText"
          @clicked="$emit('action-clicked')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue"
import { bluePurpleGradient, white } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import iconChevronUp from "@/assets/icons/icon-chevron-up.svg"
import iconChevronDown from "@/assets/icons/icon-chevron-down.svg"

// EMITS
defineEmits(["action-clicked"])

// PROPS
const props = defineProps({
  actionText: {
    type: String,
    default: null
  },
  index: {
    type: Number,
    required: true
  },
  header: {
    type: String,
    default: null
  },
  subheader: {
    type: String,
    default: null
  },
  taskKey: {
    type: String,
    required: true
  },
  isChecked: {
    type: Boolean,
    default: null
  }
})

// COMPOSABLES
const isSmOrLarger = useIsScreenSize(breakpointSm)

// DATA
const data = reactive({
  isCollapsed:
    props.taskKey === "booking"
      ? false
      : props.isChecked || !!localStorage.getItem(`checklist-${props.taskKey}-checked`),
  storageIsChecked: !!localStorage.getItem(`checklist-${props.taskKey}-checked`)
})

// COMPUTED
const isChecked = computed(() => props.isChecked || !!data.storageIsChecked)
const buttonStyle = computed(() => {
  return {
    backgroundImage: bluePurpleGradient,
    borderRadius: "8px",
    fontWeight: "500",
    textColor: white,
    width: isSmOrLarger.value ? "294px" : "100%",
    padding: "10px 25px",
    size: "medium"
  }
})

// FUNCTIONS
const handleCheckboxClick = () => {
  if (isChecked.value) {
    localStorage.removeItem(`checklist-${props.taskKey}-checked`)
    window.dispatchEvent(new CustomEvent("localstorage-checklist-item-changed", { detail: { key: props.taskKey } }))
    data.storageIsChecked = false
    data.isCollapsed = false
  } else {
    localStorage.setItem(`checklist-${props.taskKey}-checked`, "true")
    window.dispatchEvent(new CustomEvent("localstorage-checklist-item-changed", { detail: { key: props.taskKey } }))
    data.storageIsChecked = true
    data.isCollapsed = true
  }
}
</script>

<style scoped lang="scss">
.i-task-box {
  @include border-radius-md;
  @include flex(column, start, start, $gap: 6px);

  border: 1px solid $zumthor100;
  background-color: $white;
  padding: 24px;
  box-shadow: 0 14px 18px 0 #122b6905;

  .top {
    @include flex(column, start, start, $gap: 8px);
    @include text-md;

    position: relative;
    width: 100%;
    font-weight: 400;

    @media screen and (min-width: $breakpoint-md) {
      gap: 0;
    }

    .step {
      @include flex(row, start, start, $gap: 20px);

      > span {
        font-weight: 500;
        background-image: $blue-purple-gradient;
        color: transparent;
        background-clip: text;
      }

      .check {
        > input {
          /* stylelint-disable */
          -webkit-appearance: none;
          /* stylelint-enable */
          position: relative;
          width: 24px;
          height: 25px;
          border-radius: 5px;
          background: $black50;
          border: 2px solid $black300;
          cursor: pointer;
          margin: 0;

          &:checked {
            border: none;
          }

          &:checked::before {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: calc(100% + 1px);
            height: calc(100% + 1px);
            pointer-events: none;
            background-image: url("@/assets/icons/icon-check-gradient.svg");
            background-size: cover;
            background-repeat: no-repeat;
          }

          &:disabled {
            filter: opacity(0.5);
            cursor: not-allowed;
          }
        }
      }
    }

    .head {
      @include flex(row, start, center, $gap: 20px);

      font-weight: 600;
      width: 100%;
      padding-right: 44px;

      @media screen and (min-width: $breakpoint-md) {
        padding-left: 44px;
      }

      .chevron {
        width: 24px;
        height: 24px;
        flex: 0 0 24px;
        cursor: pointer;
        position: absolute;
        top: 50%;
        right: 0;
        transform: translate(0%, -50%);
      }
    }
  }

  .collapsible {
    @include flex(column, start, stretch, $gap: 20px);

    width: 100%;

    @media screen and (min-width: $breakpoint-md) {
      padding: 0 44px;
    }

    .sub {
      @include text-sm;
    }

    .action {
      @include flex(column, center, center);

      width: 100%;
    }
  }
}
</style>
